import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import LayoutSidebar from '../../../components/LayoutSidebar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { setNotification } from '../../../redux/notificationSlice'
import { useRequest } from '../../../hooks/UseRequest'
import { selectAuth } from '../../../redux/authSlice'
import Loading from '../../../components/Loading'
import Papa from 'papaparse'
import { handleErrorResponse } from '../../../helpers/errorHandling'

export default function BulkUncollectibleLoanRelease() {
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [file, setFile] = useState(null)
  const [fileRows, setFileRows] = useState([])
  const [fileValues, setFileValues] = useState([])
  const [isLoadingSampleFile, setIsLoadingSampleFile] = useState(false)
  const dispatch = useDispatch()
  const requests = useRequest()
  const auth = useSelector(selectAuth)
  const { register, handleSubmit, control, setError, formState: { errors } } = useForm()

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      setLoaded(true)
    }
  }, [auth.accessToken])

  const onSubmit = async (formData) => {
    if (!file) {
      setError('file', { type: 'manual', message: 'File is required' })
      dispatch(setNotification({ type: 'error', body: 'Please select a file to upload' }))
      return
    }

    setSaving(true)

    try {
      const body = {}
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/create_uncollectible_loan_job`)
      const response = await requests.modify(body, file)

      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        dispatch(setNotification({ type: 'success', body: 'Loan list uploaded successfully' }))
        // Reset form
        setFile(null)
        setFileRows([])
        setFileValues([])
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      handleErrorResponse(error.response?.data?.errors, setError)
      const errorMessage = error?.response?.data?.message ?? 'Failed to upload file'
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const handleGetSampleFileClick = async () => {
    setIsLoadingSampleFile(true)
    try {
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/download_sample_file/bulk_uncollectible_loan_release.csv`
      requests.setEndpoint(endpoint)
      const response = await requests.get()
      if (response.data.success) {
        window.open(response.data.data.url, '_blank')
      } else {
        dispatch(setNotification({ type: 'error', body: 'Failed to get download file!' }))
      }
      setIsLoadingSampleFile(false)
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: 'Failed to get download file!' }))
      setIsLoadingSampleFile(false)
    }
  }

  if (!loaded) {
    return <Loading />
  }

  return (
    <LayoutSidebar
      contentTitle="Bulk Uncollectible Loan Release"
      content={(
        <div className="container is-fluid pr-0">
          <form onSubmit={handleSubmit(onSubmit)} className="form lox-form mt-3">
            <div className="field is-horizontal">
              <label className="dmc-modal-label">
                Loan List File:
                <span
                  style={{ pointerEvents: 'auto' }}
                  className="lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal"
                  data-tooltip="Supported file types: .csv. The file should contain a 'Loan ID' column."
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className="pl-1 has-text-link"
                  />
                </span>
              </label>
              <div className="control" style={{ width: '800px' }}>
                <Controller
                  control={control}
                  name="file"
                  rules={{ required: true }}
                  render={({ field: { value, onChange, ...field } }) => {
                    return (
                      <input
                        {...field}
                        value={value?.fileName}
                        accept=".csv"
                        onChange={(event) => {
                          const selectedFile = event.target.files[0]
                          onChange(selectedFile)
                          setFile(selectedFile)
                          if (selectedFile) {
                            Papa.parse(selectedFile, {
                              header: true,
                              complete: (results) => {
                                const rowsArray = []
                                const valuesArray = []

                                // Iterating data to get column name and their values
                                results?.data?.map((d) => {
                                  rowsArray.push(Object.keys(d))
                                  valuesArray.push(Object.values(d))
                                })
                                setFileRows(rowsArray[0])
                                setFileValues(valuesArray)
                              },
                              error: (err) => {
                                console.log(err)
                                dispatch(setNotification({ type: 'error', body: 'Error parsing CSV file' }))
                              },
                            })
                          } else {
                            setFileRows([])
                            setFileValues([])
                          }
                        }}
                        type="file"
                      />
                    )
                  }}
                />
                <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                  {errors.file && <span>{errors.file.message ? errors.file.message : 'File is required'}</span>}
                </p>
                <a
                  className="mt-2"
                  onClick={handleGetSampleFileClick}
                  style={{ cursor: 'pointer', color: '#3273dc' }}
                >
                  {isLoadingSampleFile ? 'Loading...' : 'Sample File'}
                </a>
              </div>
            </div>

            {file && (
              <div className="field is-horizontal">
                <label className="dmc-modal-label">File Review:</label>
                <div className="control" style={{ width: '800px' }}>
                  <div className="columns" style={{ margin: 0 }}>
                    <div className="column" style={{ paddingRight: '1rem' }}>
                      <div
                        className="lox-has-border-top"
                        style={{ maxHeight: '200px', overflow: 'auto' }}
                      >
                        <table className="table is-striped is-narrow is-bordered is-hoverable is-fullwidth is-info">
                          <thead>
                            <tr>
                              {fileRows?.map((rows) => {
                                return <th key={rows}>{rows}</th>
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {fileValues?.map((value) => {
                              // Use all values combined as unique key for the row
                              const rowKey = value.join('-') || `row-${Math.random()}`
                              return (
                                <tr key={rowKey}>
                                  {fileRows?.map((columnName) => {
                                    // Get the value for this column by finding its index
                                    const columnIndex = fileRows.indexOf(columnName)
                                    const cellValue = value[columnIndex] || ''
                                    // Use column name and cell value for unique key
                                    return <td key={`${columnName}-${cellValue}`}>{cellValue}</td>
                                  })}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="column is-narrow" style={{ display: 'flex', alignItems: 'flex-start', paddingLeft: '1rem' }}>
                      <button
                        type="submit"
                        className="button is-link has-text-white header-button px-4"
                        disabled={saving || !file}
                      >
                        {saving ? <Loading /> : 'Upload List'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      )}
    />
  )
}
